// Enhanced Sound service with voice lines and audio files
interface SoundSettings {
  enabled: boolean;
  voiceEnabled: boolean;
  voiceVolume: number; // 0-1
  sfxVolume: number; // 0-1
  musicVolume: number; // 0-1
  musicTheme: string; // Selected music theme
  musicEnabled: boolean; // Background music toggle
}

// Available music themes
export const MUSIC_THEMES = [
  { id: 'none', name: 'No Music', file: '' },
  { id: 'dark_aria', name: 'Dark Aria', file: 'background_theme/Dark_Aria.mp3' },
  // Add more themes here as you add more music files
] as const;

class SoundService {
  private audioContext: AudioContext | null = null;
  private settings: SoundSettings = {
    enabled: true,
    voiceEnabled: true,
    voiceVolume: 0.4, // Reduced from 0.7 to 0.4
    sfxVolume: 0.3,   // Reduced from 0.5 to 0.3
    musicVolume: 0.2, // Reduced from 0.3 to 0.2
    musicTheme: 'none',
    musicEnabled: false,
  };
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private currentMusic: HTMLAudioElement | null = null;
  private currentOnboardingVoice: HTMLAudioElement | null = null;
  private musicPauseHandler: (() => void) | null = null;
  private musicEndedHandler: (() => void) | null = null;
  private hasUserInteracted: boolean = false;
  private pendingMusicStart: boolean = false;

  constructor() {
    // Initialize AudioContext on first user interaction
    if (typeof window !== 'undefined') {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.loadSettings();
      this.setupUserInteractionListeners();
    }
  }

  // Setup listeners for user interaction to enable autoplay
  private setupUserInteractionListeners(): void {
    const enableAutoplay = () => {
      this.hasUserInteracted = true;
      
      // If music was pending, start it now
      if (this.pendingMusicStart) {
        this.pendingMusicStart = false;
        this.restartBackgroundMusic();
      }
      
      // Remove listeners after first interaction
      document.removeEventListener('click', enableAutoplay);
      document.removeEventListener('keydown', enableAutoplay);
      document.removeEventListener('touchstart', enableAutoplay);
    };

    document.addEventListener('click', enableAutoplay, { once: true });
    document.addEventListener('keydown', enableAutoplay, { once: true });
    document.addEventListener('touchstart', enableAutoplay, { once: true });
  }

  // Load settings from localStorage
  private loadSettings(): void {
    const saved = localStorage.getItem('soundSettings');
    if (saved) {
      this.settings = { ...this.settings, ...JSON.parse(saved) };
    }
  }

  // Save settings to localStorage
  private saveSettings(): void {
    localStorage.setItem('soundSettings', JSON.stringify(this.settings));
  }

  // Get current settings
  getSettings(): SoundSettings {
    return { ...this.settings };
  }

  // Update settings
  updateSettings(newSettings: Partial<SoundSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
    
    // Update current music volume if playing
    if (this.currentMusic) {
      this.currentMusic.volume = this.settings.musicVolume;
    }

    // If music theme changed or music enabled changed, restart music
    if ('musicTheme' in newSettings || 'musicEnabled' in newSettings) {
      this.restartBackgroundMusic();
    }
  }

  // Enable/disable sounds
  setEnabled(enabled: boolean): void {
    this.settings.enabled = enabled;
    this.saveSettings();
  }

  // Play audio file (voice or sfx)
  private async playAudio(path: string, volume: number, type: 'voice' | 'sfx' = 'sfx'): Promise<void> {
    if (!this.settings.enabled) return;
    if (type === 'voice' && !this.settings.voiceEnabled) return;

    try {
      let audio = this.audioCache.get(path);
      
      if (!audio) {
        audio = new Audio(path);
        audio.preload = 'auto';
        this.audioCache.set(path, audio);
      }

      // Clone the audio to allow overlapping sounds
      const audioClone = audio.cloneNode() as HTMLAudioElement;
      audioClone.volume = volume * (type === 'voice' ? this.settings.voiceVolume : this.settings.sfxVolume);
      
      await audioClone.play();
    } catch (error) {
      console.warn(`Failed to play audio: ${path}`, error);
    }
  }

  // Play voice line
  private playVoice(filename: string): void {
    this.playAudio(`/sounds/voice/${filename}`, 0.6, 'voice'); // Reduced from 0.8 to 0.6
  }

  // Play sound effect
  private playSFX(filename: string, volume: number = 0.4): void { // Reduced default from 0.6 to 0.4
    this.playAudio(`/sounds/voice/sfx/${filename}`, volume, 'sfx');
  }

  // Play sound effect with duration limit
  private async playSFXWithDuration(filename: string, volume: number, durationSeconds: number): Promise<void> {
    if (!this.settings.enabled) return;

    try {
      let audio = this.audioCache.get(`/sounds/voice/sfx/${filename}`);
      
      if (!audio) {
        audio = new Audio(`/sounds/voice/sfx/${filename}`);
        audio.preload = 'auto';
        this.audioCache.set(`/sounds/voice/sfx/${filename}`, audio);
      }

      // Clone the audio to allow overlapping sounds
      const audioClone = audio.cloneNode() as HTMLAudioElement;
      audioClone.volume = volume * this.settings.sfxVolume;
      
      await audioClone.play();
      
      // Stop the audio after the specified duration
      setTimeout(() => {
        audioClone.pause();
        audioClone.currentTime = 0;
      }, durationSeconds * 1000);
    } catch (error) {
      console.warn(`Failed to play audio: ${filename}`, error);
    }
  }

  // Play beep fallback (for compatibility)
  private playBeep(frequency: number, duration: number, volume: number = 0.2): void { // Reduced from 0.3 to 0.2
    if (!this.settings.enabled || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      const adjustedVolume = volume * this.settings.sfxVolume;
      gainNode.gain.setValueAtTime(adjustedVolume, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch (error) {
      console.warn('Sound playback failed:', error);
    }
  }

  // ========== PUBLIC METHODS ==========

  // Quest completion sound (voice + sfx)
  questComplete(): void {
    this.playVoice('quest_completed.mp3');
    this.playSFX('quest_complete.mp3', 0.3); // Reduced from 0.5 to 0.3
  }

  // Level up sound (voice + sfx)
  levelUp(): void {
    this.playVoice('level_up.mp3');
    this.playSFX('level_up.mp3', 0.25); // Further reduced from 0.4 to 0.25
  }

  // Achievement unlocked sound
  achievementUnlocked(): void {
    this.playVoice('achievement_unlocked.mp3');
    this.playSFX('achievement_epic.mp3', 0.2); // Further reduced from 0.3 to 0.2
  }

  // Daily quest available
  dailyQuestAvailable(): void {
    this.playVoice('daily_quest_available.mp3');
    this.playSFX('menu_open.mp3', 0.4);
  }

  // Daily reward claimed
  dailyReward(): void {
    this.playVoice('daily_reward.mp3');
    this.playSFXWithDuration('coin_collect.mp3', 0.3, 0.5); // Play for 0.5 seconds max
  }

  // Penalty active warning
  penaltyActive(): void {
    this.playVoice('penalty_active.mp3');
    this.playSFX('penalty_warning.mp3', 0.4); // Reduced from 0.6 to 0.4
  }

  // Penalty cleared
  penaltyCleared(): void {
    this.playVoice('penalty_cleared.mp3');
    this.playSFX('achievement_epic.mp3', 0.3); // Reduced from 0.5 to 0.3
  }

  // Quest failed
  questFailed(): void {
    this.playVoice('quest_failed.mp3');
    this.playSFX('penalty_warning.mp3', 0.3);
  }

  // Stat points allocated
  statAllocated(): void {
    this.playVoice('stat_allocated.mp3');
    this.playSFX('stat_boost.mp3', 0.5);
  }

  // Passive Skill: Training established (creating new skill)
  trainingEstablished(): void {
    this.playVoice('training_established.mp3'); // Note: filename has typo but keeping as is
    this.playSFX('stat_boost.mp3', 0.4);
  }

  // Training Program created
  trainingProgramCreated(): void {
    this.playVoice('training_established.mp3');
    this.playSFX('achievement_epic.mp3', 0.3);
  }

  // Passive Skill: Training complete (daily check-in)
  trainingComplete(): void {
    this.playVoice('training_complete.mp3');
    this.playSFXWithDuration('coin_collect.mp3', 0.3, 0.5); // Play for 0.5 seconds max
  }

  // Passive Skill: Training terminated (deleting skill)
  trainingTerminated(): void {
    this.playVoice('training_terminated.mp3');
    this.playSFX('button_click.mp3', 0.3);
  }

  // Title/Rank acquired
  titleAcquired(rank: string): void {
    this.playVoice('title_acquired.mp3');
    
    // Play rank-specific voice line after a delay
    setTimeout(() => {
      const rankFile = `rank_${rank.toLowerCase()}.mp3`;
      this.playVoice(rankFile);
    }, 1000);
    
    this.playSFX('achievement_epic.mp3', 0.7);
  }

  // Shadow Monarch awakening (Level 100)
  shadowMonarch(): void {
    this.playVoice('shadow_monarch.mp3');
    this.playSFX('achievement_epic.mp3', 1.0);
  }

  // Onboarding tutorial intro
  async onboardingIntro(): Promise<void> {
    if (!this.settings.enabled || !this.settings.voiceEnabled) return;
    
    // Stop any existing onboarding voice
    this.stopOnboardingVoice();
    
    try {
      const path = '/sounds/voice/onboard_tutorial.mp3';
      let audio = this.audioCache.get(path);
      
      if (!audio) {
        audio = new Audio(path);
        audio.preload = 'auto';
        this.audioCache.set(path, audio);
      }

      // Clone the audio to allow overlapping sounds
      const audioClone = audio.cloneNode() as HTMLAudioElement;
      audioClone.volume = 0.8 * this.settings.voiceVolume;
      
      this.currentOnboardingVoice = audioClone;
      
      // Clean up reference when audio ends
      audioClone.onended = () => {
        if (this.currentOnboardingVoice === audioClone) {
          this.currentOnboardingVoice = null;
        }
      };
      
      await audioClone.play();
    } catch (error) {
      console.warn('Failed to play onboarding voice:', error);
    }
  }

  // Stop onboarding voice
  stopOnboardingVoice(): void {
    if (this.currentOnboardingVoice) {
      this.currentOnboardingVoice.pause();
      this.currentOnboardingVoice.currentTime = 0;
      this.currentOnboardingVoice = null;
    }
  }

  // Focus mode start
  focusStart(): void {
    this.playSFX('focus_start.mp3', 0.5);
  }

  // Focus mode end
  focusEnd(): void {
    this.playSFX('focus_end.mp3', 0.5);
  }

  // Button click
  buttonClick(): void {
    this.playSFX('button_click.mp3', 0.3);
  }

  // Menu open
  menuOpen(): void {
    this.playSFX('menu_open.mp3', 0.4);
  }

  // Coin collect
  coinCollect(): void {
    this.playSFXWithDuration('coin_collect.mp3', 0.4, 0.5); // Play for 0.5 seconds max
  }

  // Notification sound
  notification(): void {
    this.playSFX('menu_open.mp3', 0.3);
  }

  // Streak milestone sound
  streakMilestone(): void {
    this.playSFX('achievement_epic.mp3', 0.6);
  }

  // ========== MUSIC CONTROL ==========

  // Set and play music theme
  setMusicTheme(themeId: string): void {
    this.updateSettings({ musicTheme: themeId });
  }

  // Get current music theme
  getMusicTheme(): string {
    return this.settings.musicTheme;
  }

  // Restart background music based on current settings
  private restartBackgroundMusic(): void {
    this.stopMusic();

    if (!this.settings.enabled || !this.settings.musicEnabled || this.settings.musicTheme === 'none') {
      return;
    }

    const theme = MUSIC_THEMES.find(t => t.id === this.settings.musicTheme);
    if (theme && theme.file) {
      this.playMusic(theme.file);
    }
  }

  // Play background music (looping)
  private playMusic(filename: string): void {
    if (!this.settings.enabled) return;

    // If user hasn't interacted yet, mark music as pending and wait
    if (!this.hasUserInteracted) {
      this.pendingMusicStart = true;
      return;
    }

    try {
      // Stop current music
      this.stopMusic();

      const audio = new Audio(`/sounds/voice/${filename}`);
      audio.loop = true;
      audio.volume = this.settings.musicVolume;
      
      // Create and store event handlers
      this.musicPauseHandler = () => {
        // If music was paused unexpectedly (not by stopMusic), resume it
        if (this.currentMusic === audio && this.settings.musicEnabled && this.settings.enabled) {
          setTimeout(() => {
            if (this.currentMusic === audio) {
              audio.play().catch(err => console.warn('Failed to resume music:', err));
            }
          }, 100);
        }
      };

      this.musicEndedHandler = () => {
        // This shouldn't happen with loop=true, but just in case
        if (this.currentMusic === audio && this.settings.musicEnabled && this.settings.enabled) {
          audio.play().catch(err => console.warn('Failed to restart music:', err));
        }
      };

      // Add event listeners to handle unexpected pauses
      audio.addEventListener('pause', this.musicPauseHandler);
      audio.addEventListener('ended', this.musicEndedHandler);

      audio.play().catch(error => {
        console.warn('Failed to auto-play music (browser may require user interaction):', error);
      });
      
      this.currentMusic = audio;
    } catch (error) {
      console.warn('Failed to play music:', error);
    }
  }

  // Stop background music
  stopMusic(): void {
    // Clear any pending music start
    this.pendingMusicStart = false;
    
    if (this.currentMusic) {
      // Remove event listeners to prevent auto-resume
      const audio = this.currentMusic;
      
      if (this.musicPauseHandler) {
        audio.removeEventListener('pause', this.musicPauseHandler);
        this.musicPauseHandler = null;
      }
      
      if (this.musicEndedHandler) {
        audio.removeEventListener('ended', this.musicEndedHandler);
        this.musicEndedHandler = null;
      }
      
      this.currentMusic = null; // Set to null after removing listeners
      
      audio.pause();
      audio.currentTime = 0;
    }
  }

  // Initialize background music if enabled
  initializeMusic(): void {
    if (this.settings.musicEnabled && this.settings.musicTheme !== 'none') {
      const theme = MUSIC_THEMES.find(t => t.id === this.settings.musicTheme);
      if (theme && theme.file) {
        this.playMusic(theme.file);
      }
    }
  }

  // Preload commonly used sounds
  preloadSounds(): void {
    const commonSounds = [
      '/sounds/voice/quest_completed.mp3',
      '/sounds/voice/level_up.mp3',
      '/sounds/voice/achievement_unlocked.mp3',
      '/sounds/voice/sfx/quest_complete.mp3',
      '/sounds/voice/sfx/level_up.mp3',
      '/sounds/voice/sfx/button_click.mp3',
    ];

    commonSounds.forEach(path => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      this.audioCache.set(path, audio);
    });
  }
}

export const soundService = new SoundService();

